import axios, { AxiosInstance } from 'axios';
import { apiConfig } from '@/config/apiConfig';
import { getVoiceFallbackClient } from './_shared';

// ── pricing-svc billing data (VOICE_USAGE_API_AI_TEAM.md §6) ───────────
// Real subscription/success-fee data from pricing-svc, keyed by tenant_id —
// distinct from the local-credit-ledger endpoints above. Every function
// returns null-ish placeholder shapes (never throws) when pricing-svc or the
// tenant_id mapping is unresolved, matching the backend's fail-open posture.

export type PricingSvcTrialStatus = {
  success: boolean;
  tenant_id?: number;
  is_trial: boolean | null;
  allowed: boolean | null;
  trial_sessions_limit?: number;
  trial_sessions_used?: number;
  trial_sessions_remaining?: number;
  status?: string;
  tier?: string;
};

/** `GET /api/billing/pricing-svc/trial-status` */
export async function fetchPricingSvcTrialStatus(axiosInstance?: AxiosInstance): Promise<PricingSvcTrialStatus> {
  const client = axiosInstance || getVoiceFallbackClient();
  const response = await client.get<PricingSvcTrialStatus>(`${apiConfig.voiceEndpoints.billing}/pricing-svc/trial-status`);
  return response.data;
}

export type PricingSvcUsageSummary = {
  success: boolean;
  tenant_id?: number;
  billing_period?: string;
  tier?: string;
  total_sessions: number | null;
  webrtc_sessions?: number;
  pstn_sessions?: number;
  total_minutes_used: number | null;
  included_minutes?: number;
  overage_minutes?: number;
  overage_usd?: number;
  is_trial?: boolean;
};

/** `GET /api/billing/pricing-svc/usage-summary?billing_period=YYYY-MM` */
export async function fetchPricingSvcUsageSummary(billingPeriod?: string, axiosInstance?: AxiosInstance): Promise<PricingSvcUsageSummary> {
  const client = axiosInstance || getVoiceFallbackClient();
  const response = await client.get<PricingSvcUsageSummary>(
    `${apiConfig.voiceEndpoints.billing}/pricing-svc/usage-summary`,
    billingPeriod ? { params: { billing_period: billingPeriod } } : undefined,
  );
  return response.data;
}

export type PricingSvcSuccessFeeLineItem = {
  event_type: string;
  count: number;
  total_usd: number;
  rate_type: string;
};

export type PricingSvcSuccessFeeSummary = {
  success: boolean;
  tenant_id?: number;
  billing_period?: string;
  total_success_fees_usd: number | null;
  line_items: PricingSvcSuccessFeeLineItem[];
};

/** `GET /api/billing/pricing-svc/success-fees-summary?billing_period=YYYY-MM` */
export async function fetchPricingSvcSuccessFeeSummary(billingPeriod?: string, axiosInstance?: AxiosInstance): Promise<PricingSvcSuccessFeeSummary> {
  const client = axiosInstance || getVoiceFallbackClient();
  const response = await client.get<any>(
    `${apiConfig.voiceEndpoints.billing}/pricing-svc/success-fees-summary`,
    billingPeriod ? { params: { billing_period: billingPeriod } } : undefined,
  );
  const d = response.data ?? {};
  return {
    success: Boolean(d.success),
    tenant_id: d.tenant_id,
    billing_period: d.billing_period,
    total_success_fees_usd: d.total_success_fees_usd ?? null,
    line_items: Array.isArray(d.line_items) ? d.line_items : [],
  };
}

export type PricingSvcActionLogEntry = {
  event_id: number | string;
  event_type: string;
  session_id?: string;
  agent_id?: string;
  applied_rate: number;
  rate_type: string;
  status: string;
  created_at: string;
};

export type PricingSvcActionLog = {
  success: boolean;
  tenant_id?: number;
  billing_period?: string;
  total_count: number;
  entries: PricingSvcActionLogEntry[];
};

/** `GET /api/billing/pricing-svc/action-log?billing_period=YYYY-MM&limit=&offset=` */
export async function fetchPricingSvcActionLog(
  opts: { billingPeriod?: string; limit?: number; offset?: number } = {},
  axiosInstance?: AxiosInstance,
): Promise<PricingSvcActionLog> {
  const client = axiosInstance || getVoiceFallbackClient();
  const params: Record<string, any> = { limit: opts.limit ?? 50, offset: opts.offset ?? 0 };
  if (opts.billingPeriod) params.billing_period = opts.billingPeriod;
  const response = await client.get<any>(`${apiConfig.voiceEndpoints.billing}/pricing-svc/action-log`, { params });
  const d = response.data ?? {};
  return {
    success: Boolean(d.success),
    tenant_id: d.tenant_id,
    billing_period: d.billing_period,
    total_count: Number(d.total_count ?? 0),
    entries: Array.isArray(d.entries) ? d.entries : [],
  };
}
